/* Heatmap-VORLAGE der Player Card: typische Zonen je Position — reine Funktionen, kein Canvas.
 *
 * ACHTUNG: Das sind KEINE Messdaten und kein Tracking. Die Karte zeigt, wo ein Spieler auf dieser Position
 * typischerweise spielt; sie wird aus der ersten und zweiten Position (pos, pos2) abgeleitet und trägt die
 * Beschriftung "TYPICAL ZONES · BY POSITION".
 *
 * Koordinaten normiert: x 0 = eigenes Tor, 1 = gegnerisches Tor (Angriffsrichtung nach rechts);
 * y 0 = linke Seite (oben im Bild), 1 = rechte Seite (unten).
 */

export const ZONES = {
  GK: { x: 0.06, y: 0.5 },
  CB: { x: 0.2, y: 0.5 },
  LB: { x: 0.3, y: 0.12 },
  RB: { x: 0.3, y: 0.88 },
  DM: { x: 0.38, y: 0.5 },
  CM: { x: 0.5, y: 0.5 },
  AM: { x: 0.62, y: 0.5 },
  LW: { x: 0.7, y: 0.15 },
  RW: { x: 0.7, y: 0.85 },
  SS: { x: 0.74, y: 0.5 },
  ST: { x: 0.82, y: 0.5 },
};

/* Positionen des Hubs (domain/positions.js POSITIONS) -> Zone. Wing Backs liegen weiter vorn als die Außenverteidiger
   (Korrektur x), sonst gelten die Werte der Tabelle. */
const BY_POSITION = {
  Goalkeeper: { zone: 'GK' },
  'Center Back': { zone: 'CB' },
  'Left Back': { zone: 'LB' },
  'Right Back': { zone: 'RB' },
  'Left Wing Back': { zone: 'LB', dx: 0.08 },
  'Right Wing Back': { zone: 'RB', dx: 0.08 },
  'Defensive Midfielder': { zone: 'DM' },
  'Central Midfielder': { zone: 'CM' },
  'Offensive Midfielder': { zone: 'AM' },
  'Left Wing': { zone: 'LW' },
  'Right Wing': { zone: 'RW' },
  'Second Striker': { zone: 'SS' },
  'Central Striker': { zone: 'ST' },
};

const norm = (s) => String(s ?? '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
const EXACT = Object.fromEntries(Object.entries(BY_POSITION).map(([k, v]) => [norm(k), v]));

/**
 * Zone einer Position. Genaue Namen aus dem Hub zuerst, dann tolerant für Freitext (center/centre/central, "striker",
 * "CB" …). Mehrdeutiges ("winger", "wing" ohne Seite, "midfielder") und Unbekanntes ergibt null — keine Wolke, kein Fehler.
 * @returns {{ zone: string, x: number, y: number } | null}
 */
export function zoneForPosition(pos) {
  const s = norm(pos);
  if (!s || s === 'position tbd') return null;
  let hit = EXACT[s] || null;
  if (!hit) {
    const abbr = { gk: 'GK', cb: 'CB', lb: 'LB', rb: 'RB', lwb: 'LB', rwb: 'RB', dm: 'DM', cdm: 'DM', cm: 'CM', am: 'AM', cam: 'AM', lw: 'LW', rw: 'RW', ss: 'SS', st: 'ST', cf: 'ST' }[s];
    if (abbr) hit = { zone: abbr, dx: s === 'lwb' || s === 'rwb' ? 0.08 : 0 };
  }
  if (!hit) {
    const centre = /\b(center|centre|central)\b/.test(s);
    const left = /\bleft\b/.test(s);
    const right = /\bright\b/.test(s);
    const isBack = /\bback\b/.test(s);
    if (/goal ?keeper|\bkeeper\b/.test(s)) hit = { zone: 'GK' };
    else if (/wing ?back/.test(s) && (left || right)) hit = { zone: left ? 'LB' : 'RB', dx: 0.08 };
    else if (isBack && centre) hit = { zone: 'CB' };
    else if (isBack && left) hit = { zone: 'LB' };
    else if (isBack && right) hit = { zone: 'RB' };
    else if (/second (striker|forward)|shadow striker/.test(s)) hit = { zone: 'SS' };
    else if (/striker|centre forward|center forward|forward/.test(s) && !left && !right) hit = { zone: 'ST' };
    else if (/\bwing\b|winger/.test(s) && left && !right) hit = { zone: 'LW' };
    else if (/\bwing\b|winger/.test(s) && right && !left) hit = { zone: 'RW' };
    else if (/defensive mid|holding mid/.test(s)) hit = { zone: 'DM' };
    else if (/(attacking|offensive) mid/.test(s)) hit = { zone: 'AM' };
    else if (/central mid|centre mid|center mid/.test(s)) hit = { zone: 'CM' };
  }
  if (!hit) return null;
  const base = ZONES[hit.zone];
  return { zone: hit.zone, x: Math.min(0.95, base.x + (hit.dx || 0)), y: base.y };
}

/** Stärke der Wolken: erste Position voll, zweite schwächer (ca. 55 %). */
export const HEAT_STRENGTH = { primary: 1, secondary: 0.55 };

/**
 * Wolken der Heatmap aus erster und zweiter Position. Gleiche Zone doppelt = eine Wolke; zweite Position leer oder
 * unbekannt = nur die erste; keine bekannte Position = leere Liste (Heatmap entfällt).
 * @returns {Array<{ zone: string, x: number, y: number, strength: number }>}
 */
export function heatSpots(pos, pos2) {
  const first = zoneForPosition(pos);
  const second = zoneForPosition(pos2);
  const spots = [];
  if (first) spots.push({ ...first, strength: HEAT_STRENGTH.primary });
  if (second && !(first && second.zone === first.zone && second.x === first.x)) {
    spots.push({ ...second, strength: first ? HEAT_STRENGTH.secondary : HEAT_STRENGTH.primary });
  }
  return spots;
}

/* Wolken dürfen den Feldrand nicht hart abschneiden: Radius der Wolke (normiert auf Feldbreite/-höhe) und kleiner
   Innenabstand. Die Mitte wird so nach innen geschoben, dass die ganze Wolke im Feld liegt. */
export const HEAT_RADII = { x: 0.16, y: 0.23 };
export const HEAT_INSET = 0.012;

/** Mitte einer Wolke, so verschoben, dass ihre Bounding-Box vollständig im Feld liegt (mit Innenabstand). */
export function cloudCenter(spot) {
  const clamp = (v, r) => Math.min(1 - r - HEAT_INSET, Math.max(r + HEAT_INSET, v));
  return { x: clamp(spot.x, HEAT_RADII.x), y: clamp(spot.y, HEAT_RADII.y) };
}
